import { AlertTriangle, ExternalLink, FileCode, Play, Zap } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { StagedLink } from '@/lib/api';
import { defang } from '@/lib/preview/render';

/** Where a sample points, and what came of following it.
 *
 *  Two rules hold this component. Nothing is ever an anchor - a live link to
 *  attacker infrastructure is one misclick from beaconing the platform keeps
 *  deliberate. And every target is defanged, so a URL cannot be pasted
 *  somewhere live by accident either. Both hold for a target that was
 *  retrieved as much as for one that was not.
 *
 *  What following produced is stated per row rather than inferred from whether
 *  a payload appeared. "Nobody tried", "the host refused" and "it served a
 *  program" are three different findings about a lure, and only the first is
 *  silence. */

interface Props {
  links: StagedLink[];
  /** Resolves an entry id to its display name, so a reference inside an archive
   *  member is attributed to that member. */
  entryName: (entryId: string) => string;
}

/** What each mechanism means. An open action is the strongest: it fires when
 *  the document opens, with no interaction. */
const KIND_LABEL: Record<StagedLink['kind'], string> = {
  open_action: 'Runs on open',
  uri: 'Link',
  javascript: 'Script',
  launch: 'Launches',
  goto_remote: 'Opens remotely',
  relationship: 'External part',
};

/** What each fetch outcome means, in the analyst's terms. */
const FETCH_LABEL: Record<StagedLink['fetch_state'], string> = {
  not_attempted: 'Not followed',
  pending: 'Following…',
  fetched: 'Retrieved',
  refused: 'Refused',
  failed: 'Nothing retrieved',
};

/** The outcome badge. Refused is the one that reads as a finding about the
 *  document rather than as a fault: a target resolving somewhere this platform
 *  will not connect to is the lure telling you what it is. */
function FetchBadge({ link }: { link: StagedLink }) {
  if (link.fetch_state === 'not_attempted') return null;
  const tone =
    link.fetch_state === 'refused'
      ? 'bg-verdict-malicious/15 text-verdict-malicious'
      : link.fetch_state === 'fetched'
        ? 'bg-verdict-suspicious/15 text-verdict-suspicious'
        : 'bg-ink-850 text-ink-400';
  return (
    <span className={`rounded px-1.5 py-0.5 text-mini ${tone}`}>
      {FETCH_LABEL[link.fetch_state]}
    </span>
  );
}

function KindIcon({ kind }: { kind: StagedLink['kind'] }) {
  const cls = 'h-3.5 w-3.5 shrink-0';
  switch (kind) {
    case 'open_action':
      return <Zap className={`${cls} text-verdict-suspicious`} aria-hidden />;
    case 'javascript':
      return <FileCode className={`${cls} text-verdict-suspicious`} aria-hidden />;
    case 'launch':
      return <Play className={`${cls} text-verdict-suspicious`} aria-hidden />;
    default:
      return <ExternalLink className={`${cls} text-ink-400`} aria-hidden />;
  }
}

/** One target, and everywhere in the document it was written.
 *
 *  A document routinely repeats a target: the same anchor from ten paragraphs,
 *  the same tracking URL from every page footer. Ten rows saying the same thing
 *  is not ten findings, and it buries the one target that actually leaves the
 *  machine - which in a real sample was one row in eleven. */
interface Target {
  /** The first link for this target; carries the url, kind and fetch outcome. */
  head: StagedLink;
  /** Where it was written, in inspection order. */
  places: { id: string; owner: string; location: string }[];
}

/** Group links by target, network targets first.
 *
 *  Grouped on (url, kind): the same URL reached through an open action and
 *  through a click are different findings about the document, and only the
 *  first fires without the analyst doing anything. A link with no url at all
 *  keeps its own row, since there is nothing to group it by. */
function groupTargets(links: StagedLink[], entryName: (entryId: string) => string): Target[] {
  const byKey = new Map<string, Target>();
  const out: Target[] = [];

  for (const link of links) {
    const place = { id: link.id, owner: entryName(link.entry_id), location: link.location };
    const key = link.url ? `${link.kind} ${link.url}` : '';
    if (!key) {
      out.push({ head: link, places: [place] });
      continue;
    }
    const seen = byKey.get(key);
    if (seen) {
      seen.places.push(place);
      continue;
    }
    const target: Target = { head: link, places: [place] };
    byKey.set(key, target);
    out.push(target);
  }
  return out;
}

export default function LinkList({ links, entryName }: Props) {
  const [showInternal, setShowInternal] = useState(false);

  // Grouped once per link set: the page re-renders on every inspection poll,
  // and regrouping a document's whole reference list on each one is work with
  // no new answer.
  const { network, internal } = useMemo(() => {
    const targets = groupTargets(links, entryName);
    return {
      network: targets.filter((t) => t.head.fetchable),
      internal: targets.filter((t) => !t.head.fetchable),
    };
  }, [links, entryName]);

  if (links.length === 0) return null;

  const fetchable = links.filter((l) => l.fetchable).length;
  const retrieved = links.filter((l) => l.fetch_state === 'fetched').length;
  const following = links.some((l) => l.fetch_state === 'pending');

  return (
    <section
      className="rounded-lg border border-ink-800 bg-ink-900"
      aria-label="References found in the sample"
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-ink-800 px-4 py-2.5">
        <AlertTriangle className="h-4 w-4 text-verdict-suspicious" aria-hidden />
        <h3 className="text-prose font-medium text-ink-100">Where this sample points</h3>
        <span className="ml-auto text-mini text-ink-400">
          {links.length} reference{links.length === 1 ? '' : 's'}
          {fetchable > 0 && ` · ${fetchable} on the network`}
          {retrieved > 0 && ` · ${retrieved} retrieved`}
        </span>
      </header>

      {/* Network targets lead, always. They are the ones that leave the
          machine, and in a real sample they were one row in eleven. */}
      <ul className="divide-y divide-ink-800">
        {network.map((target) => (
          <LinkRow key={target.head.id} target={target} />
        ))}
      </ul>

      {internal.length > 0 && (
        <div className="border-t border-ink-800">
          <button
            type="button"
            onClick={() => setShowInternal((v) => !v)}
            className="w-full px-4 py-2 text-left text-mini text-ink-400 hover:text-ink-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-400"
            aria-expanded={showInternal}
          >
            {showInternal ? 'Hide' : 'Show'} {internal.length} reference
            {internal.length === 1 ? '' : 's'} that go nowhere on the network
          </button>
          {/* Collapsed rather than dropped: a reader's own internal anchors are
              real references that nothing could follow, and an analyst looking
              for what a document declares still wants them. They are simply not
              the finding, and listing them first buried the one that was. */}
          {showInternal && (
            <ul className="divide-y divide-ink-800 border-t border-ink-800">
              {internal.map((target) => (
                <LinkRow key={target.head.id} target={target} />
              ))}
            </ul>
          )}
        </div>
      )}

      <footer className="border-t border-ink-800 px-4 py-2 text-mini text-ink-500">
        {following
          ? 'Retrieving what these point at. Targets are shown defanged and are never clickable.'
          : retrieved > 0
            ? 'What was retrieved is listed in the file tree, and detonates only if you pick it. Targets are shown defanged and are never clickable.'
            : 'Targets are shown defanged and are never clickable.'}
      </footer>
    </section>
  );
}

function LinkRow({ target }: { target: Target }) {
  const [showScript, setShowScript] = useState(false);
  const [showPlaces, setShowPlaces] = useState(false);
  const link = target.head;
  const repeats = target.places.length;

  return (
    <li className="px-4 py-2.5">
      <div className="flex items-start gap-2">
        <span className="mt-0.5">
          <KindIcon kind={link.kind} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-mini font-medium text-ink-200">{KIND_LABEL[link.kind]}</span>
            <FetchBadge link={link} />
            {!link.fetchable && link.url && (
              // Said rather than hidden: a reader's internal anchors are real
              // references that nothing could follow.
              <span className="rounded bg-ink-850 px-1.5 py-0.5 text-mini text-ink-500">
                not a network target
              </span>
            )}
          </div>

          {link.url && (
            // Deliberately a <span>, never an <a>, and defanged.
            <p className="mt-1 break-all font-mono text-mini text-ink-200">{defang(link.url)}</p>
          )}

          {link.final_url && link.final_url !== link.url && (
            // Where the bytes came from after redirects. The URL in the
            // document was only the first hop, and this is the one to pivot on.
            <p className="mt-0.5 break-all font-mono text-mini text-ink-400">
              served from {defang(link.final_url)}
            </p>
          )}

          {link.fetch_reason && (
            <p className="mt-0.5 text-mini text-ink-400">{link.fetch_reason}</p>
          )}

          {/* Where it was written. One place reads inline; several collapse to
              a count, because a target repeated across a document is one
              finding written down many times and the count is the part that
              says something. Every place stays reachable - provenance is what
              an analyst matches against the tree they picked from. */}
          {repeats === 1 ? (
            <p className="mt-1 font-mono text-mini text-ink-500 wrap-anywhere">
              {target.places[0].owner} · {target.places[0].location}
            </p>
          ) : (
            <div className="mt-1">
              <button
                type="button"
                onClick={() => setShowPlaces((v) => !v)}
                className="font-mono text-mini text-ink-500 hover:text-ink-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-400"
                aria-expanded={showPlaces}
              >
                {target.places[0].owner} · written in {repeats} places
              </button>
              {showPlaces && (
                <ul className="mt-1 space-y-0.5">
                  {target.places.map((place) => (
                    <li key={place.id} className="font-mono text-mini text-ink-500 wrap-anywhere">
                      {place.location}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {link.snippet && (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={() => setShowScript((v) => !v)}
                className="text-mini text-accent-300 hover:text-accent-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-400"
                aria-expanded={showScript}
              >
                {showScript ? 'Hide script' : 'Show script'}
              </button>
              {showScript && (
                <pre className="mt-1.5 max-h-48 overflow-auto rounded border border-ink-800 bg-ink-950 p-2 font-mono text-mini leading-relaxed text-ink-200">
                  {link.snippet}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
